import { useState, useRef, useEffect } from 'react';
import { Camera, Send, Plus, Mic, Video, ChevronDown, ChevronUp, Paperclip } from 'lucide-react';
import { NotesService } from '@/lib/notesService';
import { CategoriesService } from '@/lib/categoriesService';
import { DecryptedCategory } from '@/lib/types';
import { useToast } from '@/contexts/ToastContext';
import { compressImage, formatFileSize } from '@/lib/imageCompression';
import { ProgressBar } from '@/components/ProgressBar';
import logo from '@/assets/images/logo.png';
import './Home.css';

export default function Home() {
  const [noteContent, setNoteContent] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<{ dataUrl: string; name: string; type: string } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [categories, setCategories] = useState<DecryptedCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);
  const [location, setLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [categoriesExpanded, setCategoriesExpanded] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const recognitionRef = useRef<any>(null);
  const toast = useToast();

  useEffect(() => {
    loadCategories();
    getCurrentLocation();
    initSpeechRecognition();
  }, []);

  function initSpeechRecognition() {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'pt-BR';

      recognitionRef.current.onresult = (event: any) => {
        let interimTranscript = '';
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          } else {
            interimTranscript += transcript;
          }
        }

        if (finalTranscript) {
          setNoteContent(prev => prev + finalTranscript);
        }
      };

      recognitionRef.current.onerror = (event: any) => {
        setIsRecording(false);
        if (event.error === 'not-allowed') {
          toast.error('Permissao de microfone negada');
        }
      };

      recognitionRef.current.onend = () => {
        setIsRecording(false);
      };
    }
  }

  function toggleRecording() {
    if (!recognitionRef.current) {
      toast.error('Reconhecimento de voz nao suportado neste navegador');
      return;
    }

    if (isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
    } else {
      recognitionRef.current.start();
      setIsRecording(true);
      toast.success('Gravacao iniciada');
    }
  }

  async function loadCategories() {
    const cats = await CategoriesService.getCategories();
    setCategories(cats);
  }

  function getCurrentLocation() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        () => {
        }
      );
    }
  }

  async function handleCreateCategory() {
    if (!newCategoryName.trim()) {
      toast.warning('Digite um nome para a categoria');
      return;
    }

    const category = await CategoriesService.createCategory({
      name: newCategoryName,
      color: '#00F0FF',
    });

    if (category) {
      toast.success('Categoria criada!');
      setCategories([category, ...categories]);
      setSelectedCategory(category.id);
      setNewCategoryName('');
      setShowNewCategory(false);
    } else {
      toast.error('Erro ao criar categoria');
    }
  }

  async function handleSaveNote() {
    if (!noteContent.trim()) {
      toast.warning('Digite algum conteudo para a nota');
      return;
    }

    setIsSaving(true);
    setUploadProgress(0);
    setProgressMessage('Preparando...');

    try {
      const progressCallback = (progress: number) => {
        setUploadProgress(progress);

        if (progress < 20) {
          setProgressMessage('Criptografando conteudo...');
        } else if (progress < 50) {
          setProgressMessage('Criptografando arquivos...');
        } else if (progress < 85) {
          setProgressMessage('Enviando para armazenamento seguro...');
        } else if (progress < 100) {
          setProgressMessage('Salvando nota...');
        } else {
          setProgressMessage('Concluido!');
        }
      };

      const note = await NotesService.createNote({
        content: noteContent,
        categoryId: selectedCategory,
        photoUrl: selectedPhoto || undefined,
        videoUrl: selectedVideo || undefined,
        fileUrl: selectedFile?.dataUrl,
        fileName: selectedFile?.name,
        fileType: selectedFile?.type,
        latitude: location?.latitude,
        longitude: location?.longitude,
      }, progressCallback);

      if (note) {
        toast.success('Nota salva com sucesso!');
        setNoteContent('');
        setSelectedPhoto(null);
        setSelectedVideo(null);
        setSelectedFile(null);
        setSelectedCategory(undefined);
        setUploadProgress(0);
        setProgressMessage('');
        setTimeout(() => textareaRef.current?.focus(), 100);
      } else {
        toast.error('Nao foi possivel salvar a nota');
      }
    } catch (error) {
      toast.error('Nao foi possivel salvar a nota');
    } finally {
      setIsSaving(false);
    }
  }

  async function handlePhotoSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, selecione um arquivo de imagem válido');
      return;
    }

    setIsCompressing(true);

    try {
      const result = await compressImage(file);

      setSelectedPhoto(result.compressedDataUrl);

      if (result.compressionRatio > 0) {
        toast.success(
          `Imagem comprimida: ${formatFileSize(result.originalSize)} → ${formatFileSize(result.compressedSize)} (${result.compressionRatio}% reduzido)`
        );
      } else {
        toast.success('Imagem carregada');
      }
    } catch (error) {
      toast.error('Erro ao processar imagem. Tente novamente.');
    } finally {
      setIsCompressing(false);
      e.target.value = '';
    }
  }

  function handleVideoSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setSelectedVideo(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 1024 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`Arquivo muito grande. Tamanho maximo: ${formatFileSize(maxSize)}`);
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setSelectedFile({
        dataUrl: reader.result as string,
        name: file.name,
        type: file.type || 'application/octet-stream',
      });
      toast.success(`Arquivo selecionado: ${file.name} (${formatFileSize(file.size)})`);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  return (
    <div className="home-container">
      <div className="home-header">
        <img src={logo} alt="GHPOnotes" className="home-logo" />
      </div>

      <div className="home-content">
        <div className="categories-section">
          <div
            className="categories-header"
            onClick={() => setCategoriesExpanded(!categoriesExpanded)}
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 0',
              marginBottom: '8px'
            }}
          >
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }} className="section-title">
              Categorias ({categories.length + 1})
            </h3>
            {categoriesExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
          {categoriesExpanded && (
            <>
              <div className="categories-list">
                <button
                  className={`category-chip ${!selectedCategory ? 'selected' : ''}`}
                  onClick={() => setSelectedCategory(undefined)}
                >
                  Sem Categoria
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    className={`category-chip ${selectedCategory === cat.id ? 'selected' : ''}`}
                    onClick={() => setSelectedCategory(cat.id)}
                    style={{ borderColor: cat.color }}
                  >
                    {cat.decryptedName}
                  </button>
                ))}
                <button
                  className="category-chip new-category"
                  onClick={() => setShowNewCategory(!showNewCategory)}
                >
                  <Plus size={16} />
                </button>
              </div>
              {showNewCategory && (
                <div className="new-category-form">
                  <input
                    type="text"
                    placeholder="Nome da categoria"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    className="category-input"
                    onKeyPress={(e) => e.key === 'Enter' && handleCreateCategory()}
                  />
                  <button onClick={handleCreateCategory} className="create-category-btn">
                    Criar
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="textarea-wrapper">
          <textarea
            ref={textareaRef}
            className="note-textarea"
            placeholder="Digite sua nota aqui..."
            value={noteContent}
            onChange={(e) => setNoteContent(e.target.value)}
            autoFocus
          />
        </div>

        {selectedPhoto && (
          <div className="photo-preview">
            <img src={selectedPhoto} alt="Preview" className="photo-image" />
            <button
              className="remove-photo"
              onClick={() => setSelectedPhoto(null)}
            >
              ✕
            </button>
          </div>
        )}

        {selectedVideo && (
          <div className="photo-preview">
            <video src={selectedVideo} controls className="photo-image" />
            <button
              className="remove-photo"
              onClick={() => setSelectedVideo(null)}
            >
              ✕
            </button>
          </div>
        )}

        {selectedFile && (
          <div className="photo-preview">
            <div style={{
              padding: '20px',
              background: '#f8f9fa',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              width: '100%'
            }}>
              <Paperclip size={24} color="#666" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: '600', fontSize: '14px', color: '#333', wordBreak: 'break-all' }}>
                  {selectedFile.name}
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  {selectedFile.type}
                </div>
              </div>
            </div>
            <button
              className="remove-photo"
              onClick={() => setSelectedFile(null)}
            >
              ✕
            </button>
          </div>
        )}

        {isSaving && uploadProgress > 0 && (
          <ProgressBar
            progress={uploadProgress}
            message={progressMessage}
            showPercentage={true}
          />
        )}

        <div className="actions">
          <button onClick={handleSaveNote} disabled={isSaving} className="save-button">
            {isSaving ? (
              <span>Salvando...</span>
            ) : (
              <>
                <Send size={24} />
                <span>Salvar Nota</span>
              </>
            )}
          </button>

          <div className="actions-row">
            <label className={`action-button ${isCompressing ? 'disabled' : ''}`} title="Adicionar foto">
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoSelect}
                style={{ display: 'none' }}
                disabled={isCompressing}
              />
              <Camera size={24} />
              {isCompressing && <span style={{ fontSize: '10px', marginLeft: '4px' }}>Processando...</span>}
            </label>
            <label className="action-button" title="Adicionar video">
              <input
                type="file"
                accept="video/*"
                onChange={handleVideoSelect}
                style={{ display: 'none' }}
              />
              <Video size={24} />
            </label>
            <label className="action-button" title="Adicionar arquivo">
              <input
                type="file"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <Paperclip size={24} />
            </label>
            <button
              className={`action-button ${isRecording ? 'recording' : ''}`}
              onClick={toggleRecording}
              title={isRecording ? 'Parar gravação' : 'Gravar nota por voz'}
            >
              <Mic size={24} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
